import {useCallback, useMemo, useState} from 'react';

import uniqueId from 'lodash.uniqueid';
import {useTemporaryLimitActions} from 'src/components/connector-limits-form';
import {
    DISCARD_CHANGES_SUCCESS_MESSAGE,
    MAX_NUMBER_VALUE,
    SAVE_CHANGES_SUCCESS_MESSAGE,
} from 'src/constants';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {STATSIG_GATES} from 'src/contexts/statsig-contex/constants';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {getDefaultUserMcpLimits} from 'src/openapi/admin/apis/DefaultUserLimitsApi';
import {getMcpLimits} from 'src/openapi/admin/apis/GetMcpLimitsApi';
import {useAppDispatch} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import {
    Status,
    usePushNotification,
} from '@pb/kit-component-push-notification';
import {useGateValue} from '@pb/kit-lib-statsig';

import {useUsedLimits} from '../useUsedLimits';

import {useUserMcpLimits} from './useUserMcpLimits';
import {useUserMcpTemporaryLimits} from './useUserMcpTemporaryLimits';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {Limit} from 'src/openapi/admin/models/Limit';

export const useUserConnectorLimits = (
    userId: number,
    accountId: number,
) => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const {
        canEditUserConnectorLimits,
        canViewUserConnectorLimits,
    } = useAdminAccessRight();
    const {userFieldChange} = useEventTracking();
    const {getTodayString, today} = useDateTime();
    const [busy, setBusy] = useState(false);

    const editDisabled = useMemo(
        () => !canEditUserConnectorLimits(accountId),
        [accountId, canEditUserConnectorLimits],
    );
    const isConnectorLimitsAllowed =
        canViewUserConnectorLimits(accountId);
    const isPremiumMcpEnabled = useGateValue(
        STATSIG_GATES.PREMIUM_MCP_LIMITS,
    );

    const {
        data: mcpLimitsDictionary,
        isLoading: isDictLoading,
        loadingError: dictError,
    } = useReadOnlyDataHook({
        initValue: [],
        getDispatchAction: useCallback(
            (signal: AbortSignal) =>
                apiDispatch(getMcpLimits({signal})),
            [apiDispatch],
        ),
    });

    const {
        data: defaultMcpLimits,
        isLoading: isDefaultLoading,
        loadingError: defaultError,
    } = useReadOnlyDataHook({
        initValue: [],
        getDispatchAction: useCallback(
            (signal: AbortSignal) =>
                apiDispatch(
                    getDefaultUserMcpLimits(
                        {userId},
                        {signal},
                    ),
                ),
            [apiDispatch, userId],
        ),
    });

    const {
        data: usedLimits,
        isLoading: isUsedLimitsLoading,
        loadingError: usedLimitsError,
    } = useUsedLimits({userId});

    const {
        data,
        setData,
        dataInitial,
        isLoading,
        loadingError,
        isChanged,
        saveChanges,
        discardChanges,
    } = useUserMcpLimits({userId});

    const {
        data: tempLimitsSettings,
        dataInitial: initialTempLimitsSettings,
        setData: setTempLimitsSettings,
        isLoading: isTempLoading,
        loadingError: tempLoadingError,
        isChanged: isTempChanged,
        saveChanges: saveTempLimits,
        discardChanges: discardTempLimits,
    } = useUserMcpTemporaryLimits({userId});

    const {
        changeTemporaryLimit,
        clearTemporaryLimit,
        changeTemporaryLimitMode,
        onExpiresOnChange,
    } = useTemporaryLimitActions({
        setTempLimitsSettings,
        mcpLimitsDictionary,
        trackFieldChange: userFieldChange,
        getTodayString,
    });

    const anyChanged = isChanged || isTempChanged;
    const isLoadingAny =
        isLoading
        || isDictLoading
        || isDefaultLoading
        || isUsedLimitsLoading
        || isTempLoading;
    const combinedError =
        loadingError
        || dictError
        || defaultError
        || usedLimitsError
        || tempLoadingError;

    const getDefaultValue = useCallback(
        (limitId: string): number => {
            const defaultLimit = defaultMcpLimits.find(
                l => l.limitId === limitId,
            );
            if (
                !defaultLimit
                || defaultLimit.unlimited
                || defaultLimit.value === MAX_NUMBER_VALUE
            ) {
                return MAX_NUMBER_VALUE;
            }
            return defaultLimit.value;
        },
        [defaultMcpLimits],
    );

    const getUsedValue = useCallback(
        (limitId: string): number | undefined =>
            usedLimits.find(
                l => l.limitId === limitId,
            )?.value,
        [usedLimits],
    );

    const onSave = useCallback(async (options?: SaveOptions) => {
        if (
            tempLimitsSettings.expiresOn
            && !tempLimitsSettings.limits.length
            && isTempChanged
        ) {
            onCreateNote({
                id: uniqueId(
                    'user-mcp-limits-save-error-',
                ),
                status: Status.ERROR,
                buttonProps: {
                    iconProps: {type: Icons.CLOSE},
                },
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () =>
                    'At least one temporary limit'
                    + ' must be specified',
            });
            return 'At least one temporary limit must be specified';
        }

        setBusy(true);
        try {
            const errors: string[] = [];

            if (isChanged) {
                const error = await saveChanges(dataInitial, options);
                if (error && typeof error === 'string') {
                    errors.push(error);
                }
            }

            if (isTempChanged) {
                const error = await saveTempLimits(
                    initialTempLimitsSettings.limits, options,
                );
                if (error && typeof error === 'string') {
                    errors.push(error);
                }
            }

            setBusy(false);

            if (errors.length) {
                onCreateNote({
                    id: uniqueId(
                        'user-mcp-limits-save-error-',
                    ),
                    status: Status.ERROR,
                    buttonProps: {
                        iconProps: {type: Icons.CLOSE},
                    },
                    autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                    body: () =>
                        errors.join('; ')
                        || 'Failed to save Connector Limits',
                });
            } else {
                onCreateNote({
                    id: uniqueId('user-mcp-limits-save-'),
                    status: Status.SUCCESS,
                    buttonProps: {
                        iconProps: {type: Icons.CLOSE},
                    },
                    autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                    body: () => SAVE_CHANGES_SUCCESS_MESSAGE,
                });
            }
            return errors.length ? errors.join('; ') : undefined;
        } finally {
            setBusy(false);
        }
    }, [
        tempLimitsSettings, isChanged, isTempChanged,
        saveChanges, dataInitial, saveTempLimits,
        initialTempLimitsSettings, onCreateNote,
    ]);

    const onDiscard = useCallback(() => {
        discardChanges();
        discardTempLimits();
        onCreateNote({
            id: uniqueId('user-mcp-limits-discard-'),
            status: Status.SUCCESS,
            buttonProps: {iconProps: {type: Icons.CLOSE}},
            autoHideTimeout: RESPONSE_NOTE_LIFETIME,
            body: () => DISCARD_CHANGES_SUCCESS_MESSAGE,
        });
    }, [discardChanges, discardTempLimits, onCreateNote]);

    const onLimitChange = useCallback(
        (limitId: string, newValue?: number) => {
            const description =
                mcpLimitsDictionary.find(
                    d => d.code === limitId,
                )?.description ?? limitId;
            userFieldChange({
                parameterName: description,
                section: 'Connector Limits',
                viewType: 'Existed',
            });

            const unlimited = newValue === undefined;
            const value = unlimited
                ? MAX_NUMBER_VALUE
                : newValue;

            const defaultLimit = defaultMcpLimits.find(
                l => l.limitId === limitId,
            );
            const matchesDefault = defaultLimit
                ? value === defaultLimit.value
                && unlimited === defaultLimit.unlimited
                : unlimited;

            setData(prevData => [
                ...prevData.filter(
                    l => l.limitId !== limitId,
                ),
                ...matchesDefault
                    ? []
                    : [{
                            limitId,
                            value,
                            unlimited,
                        } as Limit],
            ]);
        },
        [
            setData, mcpLimitsDictionary,
            userFieldChange, defaultMcpLimits,
        ],
    );

    const clearCustomLimit = useCallback(
        (limitId: string) => {
            setData(prevData =>
                prevData.filter(
                    l => l.limitId !== limitId,
                ),
            );
        },
        [setData],
    );

    const isDefaultLimits = useMemo(
        () => !data.length
            && tempLimitsSettings.expiresOn === '',
        [data, tempLimitsSettings.expiresOn],
    );

    const onResetToDefaults = useCallback(() => {
        setData([]);
        setTempLimitsSettings({expiresOn: '', limits: []});
    }, [setData, setTempLimitsSettings]);

    return {
        busy,
        editDisabled,
        isConnectorLimitsAllowed,
        isPremiumMcpEnabled,
        mcpLimitsDictionary,
        data,
        tempLimitsSettings,
        anyChanged,
        isLoadingAny,
        combinedError,
        today,
        isDefaultLimits,

        getDefaultValue,
        getUsedValue,
        onSave,
        onDiscard,
        onLimitChange,
        onResetToDefaults,
        changeTemporaryLimit,
        clearTemporaryLimit,
        clearCustomLimit,
        changeTemporaryLimitMode,
        onExpiresOnChange,
    };
};
